import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Trash2, Lock, LogOut, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'aln_admin_token';

function Stars({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={15}
          className={n <= value ? 'fill-[#2C3A28] text-[#2C3A28]' : 'fill-transparent text-[#C7B79A]'}
        />
      ))}
    </div>
  );
}

export default function AdminReviews() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/admin/reviews?status=pending`, { headers: authHeader() }),
        axios.get(`${API}/admin/reviews?status=approved`, { headers: authHeader() }),
      ]);
      setPending(p.data);
      setApproved(a.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [token, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError('Invalid email or password.');
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setPending([]);
    setApproved([]);
  };

  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken('');
      setLoginError('Your session expired. Please sign in again.');
    } else {
      setActionError('Something went wrong. Please try again.');
    }
  };

  const approve = async (id) => {
    setActionError('');
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`, {}, { headers: authHeader() });
      load();
    } catch (err) {
      handleAuthError(err);
    }
  };

  const reject = async (id) => {
    setActionError('');
    try {
      await axios.post(`${API}/admin/reviews/${id}/reject`, {}, { headers: authHeader() });
      load();
    } catch (err) {
      handleAuthError(err);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#2C3A28] flex items-center justify-center px-5" data-testid="admin-login">
        <form
          onSubmit={login}
          className="w-full max-w-sm bg-[#E9EBED] rounded-3xl p-8 shadow-2xl"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#EDEAE0] flex items-center justify-center text-[#2C3A28]">
            <Lock size={26} />
          </div>
          <h1 className="mt-5 font-display text-3xl font-semibold text-[#2C3A28]">Owner login</h1>
          <p className="mt-2 text-[#3B2A20]/70 text-sm">Sign in to moderate reviews.</p>
          <input
            data-testid="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="mt-6 w-full rounded-2xl border border-[#2C3A28]/15 bg-white px-5 py-3.5 outline-none focus:border-[#2C3A28]"
          />
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="mt-3 w-full rounded-2xl border border-[#2C3A28]/15 bg-white px-5 py-3.5 outline-none focus:border-[#2C3A28]"
          />
          {loginError && <p className="mt-3 text-sm text-[#2C3A28]" data-testid="admin-login-error">{loginError}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-5 w-full rounded-full bg-[#2C3A28] text-white py-3.5 font-semibold hover:bg-[#2C3A28] transition-colors disabled:opacity-60"
          >
            {busy ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  const Card = ({ r, isPending }) => (
    <div className="rounded-2xl bg-[#EDEAE0] p-5" data-testid="admin-review-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-[#2C3A28]">{r.name}</p>
          <Stars value={r.rating} />
        </div>
        <div className="flex items-center gap-2">
          {isPending && (
            <button
              onClick={() => approve(r.id)}
              data-testid="approve-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold hover:bg-[#5C6738] transition-colors"
            >
              <Check size={16} /> Approve
            </button>
          )}
          <button
            onClick={() => reject(r.id)}
            data-testid="reject-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#2C3A28]/10 text-[#2C3A28] px-4 py-2 text-sm font-semibold hover:bg-[#2C3A28]/20 transition-colors"
          >
            <Trash2 size={16} /> {isPending ? 'Reject' : 'Remove'}
          </button>
        </div>
      </div>
      <p className="mt-3 text-[#3B2A20]/80 leading-relaxed">"{r.comment}"</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#E9EBED] pt-24 pb-20" data-testid="admin-dashboard">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-4xl font-semibold text-[#2C3A28]">Review moderation</h1>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full border border-[#2C3A28]/20 px-4 py-2 text-sm font-semibold text-[#2C3A28] hover:bg-[#EDEAE0]"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>

        {actionError && (
          <p
            className="mt-4 rounded-xl bg-[#2C3A28]/10 text-[#2C3A28] px-4 py-3 text-sm font-medium"
            data-testid="admin-action-error"
          >
            {actionError}
          </p>
        )}

        <div className="mt-10">
          <h2 className="font-display text-2xl font-semibold text-[#2C3A28]">
            Pending approval ({pending.length})
          </h2>
          <div className="mt-4 space-y-3">
            {loading && <p className="text-[#3B2A20]/60">Loading...</p>}
            {!loading && pending.length === 0 && (
              <p className="text-[#3B2A20]/60" data-testid="no-pending">No reviews waiting for approval.</p>
            )}
            {pending.map((r) => (
              <Card key={r.id} r={r} isPending />
            ))}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl font-semibold text-[#2C3A28]">
            Published ({approved.length})
          </h2>
          <div className="mt-4 space-y-3">
            {!loading && approved.length === 0 && (
              <p className="text-[#3B2A20]/60">No published reviews yet.</p>
            )}
            {approved.map((r) => (
              <Card key={r.id} r={r} isPending={false} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
